import axios from 'axios';
import type {
  Network,
  AddressInfo,
  APIBalance,
  APITransactionList,
  BitcoinAddressInfo,
} from '../types/blockchain.types';

export class BlockchainAPI {
  private readonly SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://ctucdcxnvyebmbjnruxs.supabase.co';
  private readonly ETHERSCAN_PROXY_URL = `${this.SUPABASE_URL}/functions/v1/etherscan-proxy`;

  constructor() {
    // La API key de Etherscan se proxea por Edge Function de Supabase
    // (etherscan-proxy) para no exponerla en el bundle del frontend.
  }

  public async getAddressInfo(address: string, network: Network): Promise<AddressInfo> {
    try {
      switch (network.address_format) {
        case 'evm':
          return await this.getEVMAddressInfo(address, network);
        case 'bitcoin':
          return await this.getBitcoinAddressInfo(address, network);
        case 'base58':
          return await this.getSolanaAddressInfo(address, network);
        default:
          throw new Error(`Unsupported address format: ${network.address_format}`);
      }
    } catch (error) {
      console.error(`Error fetching address info for ${network.name}:`, error);
      throw error;
    }
  }

  private async getEVMAddressInfo(address: string, network: Network): Promise<AddressInfo> {
    try {
      // Mapear network.id a los nombres que espera la Edge Function
      const networkName = network.id === 'ethereum' ? 'ethereum' :
                          network.id === 'avalanche' ? 'avalanche' : null;

      if (!networkName) {
        throw new Error(`Unsupported EVM network: ${network.id}`);
      }

      // Llamar a la Edge Function para balance
      const balanceResponse = await axios.post<APIBalance>(this.ETHERSCAN_PROXY_URL, {
        network: networkName,
        endpoint: 'balance',
        address,
      });

      // Llamar a la Edge Function para transacciones
      const txListResponse = await axios.post<APITransactionList>(this.ETHERSCAN_PROXY_URL, {
        network: networkName,
        endpoint: 'txlist',
        address,
      });

      if (balanceResponse.data.status !== '1') {
        throw new Error(`API error: ${balanceResponse.data.message}`);
      }

      const balanceWei = balanceResponse.data.result;
      const balanceEth = (Number(balanceWei) / 1e18).toFixed(6);

      const transactions = txListResponse.data.result || [];
      const firstTx = transactions.length > 0 ? transactions[0] : null;
      const lastTx = transactions.length > 0 ? transactions[transactions.length - 1] : null;

      return {
        address,
        network,
        balance: `${balanceEth} ${network.native_currency}`,
        first_tx_timestamp: firstTx ? Number(firstTx.timeStamp) : undefined,
        last_tx_timestamp: lastTx ? Number(lastTx.timeStamp) : undefined,
        tx_count: transactions.length,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        throw new Error('Rate limit exceeded. Please try again in a moment.');
      }
      throw error;
    }
  }

  private async getBitcoinAddressInfo(address: string, network: Network): Promise<AddressInfo> {
    const url = `${network.api_url}/balance?active=${address}`;

    try {
      const response = await axios.get<BitcoinAddressInfo>(url);

      const addressData = response.data[address];

      if (!addressData) {
        throw new Error('Address not found');
      }

      const balanceBTC = (addressData.final_balance / 1e8).toFixed(8);

      return {
        address,
        network,
        balance: `${balanceBTC} ${network.native_currency}`,
        tx_count: addressData.n_tx,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        throw new Error('Rate limit exceeded. Please try again in a moment.');
      }
      throw error;
    }
  }

  private async getSolanaAddressInfo(address: string, network: Network): Promise<AddressInfo> {
    const url = `${network.api_url}/account/${address}`;

    try {
      const response = await axios.get(url);

      const lamports = response.data.lamports || 0;
      const balanceSOL = (lamports / 1e9).toFixed(6);

      return {
        address,
        network,
        balance: `${balanceSOL} ${network.native_currency}`,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        throw new Error('Rate limit exceeded. Please try again in a moment.');
      }

      return {
        address,
        network,
        balance: `0.000000 ${network.native_currency}`,
      };
    }
  }

  public getExplorerUrl(address: string, network: Network): string {
    if (network.id === 'bitcoin') {
      return `${network.explorer_url}/address/${address}`;
    }

    return `${network.explorer_url}/address/${address}`;
  }
}

export const blockchainApi = new BlockchainAPI();
